/* eslint-disable react-hooks/exhaustive-deps */
import { ArrowRight, ChevronLeft } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import sakhi_empty_cart_view from "../../Assets/Images/services/sakhi_empty_cart_view.png";
import LoginModal from "../../components/auth/LoginModal";
import AddressSelectionCard from "../../components/UserSideComponents/bookings/AddressSelectionCard";
import BookingConfirmationCard from "../../components/UserSideComponents/bookings/BookingConfirmationCard";
import CartStep from "../../components/UserSideComponents/bookings/CartStep";
import PayAfterServiceCard from "../../components/UserSideComponents/bookings/PayAfterServiceCard";
import SlotSelectionCard from "../../components/UserSideComponents/bookings/SlotSelectionCard";
import { useAuth } from "../../context/AuthContext";
import { useCart } from "../../context/CartContext";

const STEPS = {
  CART: "cart",
  ADDRESS: "address",
  SLOT: "slot",
  PAYMENT: "payment",
  SUCCESS: "success",
};

const STEP_ORDER = [
  STEPS.CART,
  STEPS.ADDRESS,
  STEPS.SLOT,
  STEPS.PAYMENT,
  STEPS.SUCCESS,
];

const STEP_META = {
  [STEPS.CART]: { title: "Service Cart", subtitle: "Review services" },
  [STEPS.ADDRESS]: {
    title: "Service Location",
    subtitle: "Where should we come?",
  },
  [STEPS.SLOT]: { title: "Select Slot", subtitle: "Choose date & time" },
  [STEPS.PAYMENT]: {
    title: "Confirm Booking",
    subtitle: "Pay after service completion",
  },
  [STEPS.SUCCESS]: { title: "Confirmed", subtitle: "Booking successful" },
};

const INITIAL_BOOKING_DATA = {
  userId: null,
  slotAvailabilityId: null,
  slotSnapshot: null,
  location: {
    type: "Point",
    coordinates: [],
    address: {
      formatted: "",
      street: "",
      locality: "",
      city: "",
      state: "",
      country: "",
      pincode: "",
    },
  },
  __addressValid: false,
  __slotValid: false,
  paymentMethod: "UPI_QR",
  payment: {},
};

const UserServiceCart = ({ variant = "page" }) => {
  const isPanel = variant === "panel";
  const isPage = variant === "page";

  const navigate = useNavigate();
  const location = useLocation();

  const { cart, upsertCartItem, totals } = useCart();
  const { user, isAuthenticated } = useAuth();

  const [showLogin, setShowLogin] = useState(false);
  const [bookingData, setBookingData] = useState(INITIAL_BOOKING_DATA);
  const [isMobile, setIsMobile] = useState(false);
  const [bookingCompleted, setBookingCompleted] = useState(false);
  const [confirmItem, setConfirmItem] = useState(null);

  const params = new URLSearchParams(location.search);
  const stepFromURL = params.get("step");
  const step = STEP_ORDER.includes(stepFromURL) ? stepFromURL : STEPS.CART;

  const hasConfirmedBooking =
    bookingCompleted || sessionStorage.getItem("bookingCompleted") === "1";

  const goToStep = (nextStep, options = {}) => {
    navigate(`?step=${nextStep}`, { replace: options.replace });
  };

  /* RESPONSIVE CHECK */
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    const check = () => setIsMobile(window.innerWidth < 1024);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // useEffect(() => {
  //   console.log('bookingData:', bookingData);
  // }, [bookingData])

  useEffect(() => {
    const completed = sessionStorage.getItem("bookingCompleted") === "1";

    if (completed && !bookingCompleted) {
      setBookingCompleted(true);
    }

    if (bookingCompleted) {
      sessionStorage.setItem("bookingCompleted", "1");
    }
  }, [bookingCompleted]);

  useEffect(() => {
    if (user) {
      setBookingData((prev) => ({
        ...prev,
        userId: user.id || user._id,
      }));
    }
  }, [user]);

  useEffect(() => {
    //  SUCCESS is allowed only if booking is confirmed
    if (step === STEPS.SUCCESS && !hasConfirmedBooking) {
      navigate("?step=cart", { replace: true });
      return;
    }

    //  Empty cart cannot access flow steps (except success)
    if (cart.length === 0 && !hasConfirmedBooking && step !== STEPS.CART) {
      navigate("?step=cart", { replace: true });
      return;
    }

    //  Slot without address
    if (step === STEPS.SLOT && !bookingData.__addressValid) {
      navigate("?step=address", { replace: true });
      return;
    }

    //  Payment without slot
    if (step === STEPS.PAYMENT && !bookingData.__slotValid) {
      navigate("?step=slot", { replace: true });
    }
  }, [
    step,
    cart.length,
    bookingData.__addressValid,
    bookingData.__slotValid,
    hasConfirmedBooking,
  ]);

  useEffect(() => {
    //  Reset booking state when user starts a fresh flow
    if (step === STEPS.CART && cart.length === 0 && hasConfirmedBooking) {
      sessionStorage.removeItem("bookingCompleted");
      setBookingCompleted(false);
    }
  }, [step, cart.length, hasConfirmedBooking]);

  if (cart.length === 0 && step !== STEPS.SUCCESS && !hasConfirmedBooking) {
    return (
      <div
        className={`w-full ${isPanel
            ? "bg-white rounded-xl shadow-sm"
            : "bg-brandAccentColor/15 min-h-screen"
          }`}
      >
        <EmptyCartView variant={variant} />
      </div>
    );
  }

  /* PROCEED HANDLER */
  const handleProceed = () => {
    if (!isAuthenticated) {
      setShowLogin(true);
      return;
    }

    if (step === STEPS.CART) {
      goToStep(STEPS.ADDRESS);
    } else if (step === STEPS.ADDRESS && bookingData.__addressValid) {
      goToStep(STEPS.SLOT);
    } else if (step === STEPS.SLOT && bookingData.__slotValid) {
      goToStep(STEPS.PAYMENT);
    }
  };

  /* QTY DECREASE HANDLER */
  const handleDecrease = (item) => {
    if (item.qty === 1) {
      setConfirmItem(item);
      return;
    }

    upsertCartItem({ ...item, qty: item.qty - 1 });
  };

  /* CONFIRM REMOVE */
  const confirmRemove = () => {
    upsertCartItem({ ...confirmItem, qty: 0 });
    setConfirmItem(null);
  };

  const handlePanelBack = () => {
    const currentIndex = STEP_ORDER.indexOf(step);
    const prevStep = STEP_ORDER[currentIndex - 1];

    if (prevStep) {
      goToStep(prevStep);
    }
  };

  const HIDE_BOTTOM_BAR_STEPS = [STEPS.PAYMENT, STEPS.SUCCESS];
  const showBottomBar = !HIDE_BOTTOM_BAR_STEPS.includes(step);

  const isCTADisabled = () => {
    if (step === STEPS.CART) {
      return !totals.isMinOrderReached;
    }
    if (step === STEPS.ADDRESS) return !bookingData.__addressValid;
    if (step === STEPS.SLOT) return !bookingData.__slotValid;
    return false;
  };

  const getCTALabel = () => {
    if (!isAuthenticated) return "Login";

    switch (step) {
      case STEPS.CART:
        return "Select Address";
      case STEPS.ADDRESS:
        return "Select Slot";
      case STEPS.SLOT:
        return "Proceed to Pay";
      default:
        return "Continue";
    }
  };

  return (
    <div
      className={`flex flex-col w-full min-w-0 ${isPanel
          ? "bg-white rounded-xl shadow-sm"
          : "bg-brandAccentColor/15 min-h-screen"
        }`}
    >
      {/* PANEL HEADER (DESKTOP ONLY) */}
      {isPanel && step !== STEPS.SUCCESS && (
        <div className="px-6 py-4 border-b bg-white rounded-t-xl">
          <div className="flex items-start gap-3">
            {/* Back */}
            {step !== STEPS.CART && (
              <button
                onClick={handlePanelBack}
                className="
            mt-0.5
            p-2 rounded-lg
            bg-gray-100 text-gray-600
            hover:bg-gray-200 hover:text-gray-800
            transition
          "
              >
                <ChevronLeft size={18} />
              </button>
            )}

            {/* Titles */}
            <div className="flex flex-col">
              <h2 className="text-lg font-semibold text-gray-900 leading-tight">
                {STEP_META[step].title}
              </h2>

              <p className="text-sm text-gray-500 mt-1">
                {STEP_META[step].subtitle}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* HEADER */}
      {isPage && step !== STEPS.SUCCESS && (
        <div className="flex  bg-primaryColor/10 backdrop-blur border-b border-primaryColor/10 items-center gap-1 px-3 py-3  shadow-sm">
          <button
            onClick={() => {
              if (step === STEPS.SUCCESS) return;
              const currentIndex = STEP_ORDER.indexOf(step);
              const prevStep = STEP_ORDER[currentIndex - 1];

              if (!prevStep) {
                navigate(-1);
              } else {
                goToStep(prevStep);
              }
            }}
            className="p-1"
          >
            <ChevronLeft size={20} />
          </button>

          <h1 className="text-base font-bold capitalize">
            {STEP_META[step].title}
          </h1>
        </div>
      )}

      {/* CONTENT */}
      <div className="flex-1 overflow-y-auto p-4 min-w-0 no-scrollbar">
        {/* STEP 1: CART */}
        {step === STEPS.CART && (
          <CartStep
            cart={cart}
            totals={totals}
            onDecrease={handleDecrease}
            onIncrease={(item) =>
              upsertCartItem({ ...item, qty: item.qty + 1 })
            }
          />
        )}

        {step === STEPS.ADDRESS && (
          <AddressSelectionCard
            bookingData={bookingData}
            setBookingData={setBookingData}
          />
        )}

        {/* STEP 3: SLOT */}
        {step === STEPS.SLOT && (
          <SlotSelectionCard
            bookingData={bookingData}
            setBookingData={setBookingData}
            isPage={isPage}
          />
        )}

        {/* STEP 4: PAYMENT */}
        {step === STEPS.PAYMENT && (
          <PayAfterServiceCard
            bookingData={bookingData}
            setBookingData={setBookingData}
            onBack={() => goToStep(STEPS.SLOT)}
            onSuccess={() => {
              sessionStorage.setItem("bookingCompleted", "1");
              setBookingCompleted(true);
              goToStep(STEPS.SUCCESS, { replace: true });
            }}
          />
        )}

        {/* STEP 5: SUCCESS */}
        {step === STEPS.SUCCESS && <BookingConfirmationCard />}
      </div>
      {/* COMMON BOTTOM BAR */}
      {showBottomBar && isMobile && isPage && (
        <div className="sticky bottom-0 z-30">
          {/* MIN ORDER INFO STRIP */}
          {step === STEPS.CART && !totals.isMinOrderReached && (
            <div
              className="
          bg-orange-50 border-t border-orange-200
          px-4 py-2
          text-center
        "
            >
              <p className="text-[0.75rem] font-medium text-orange-700">
                Add{" "}
                <span className="font-bold">₹{totals.remainingToMinOrder}</span>{" "}
                more to reach the minimum order value
              </p>
            </div>
          )}

          {/* MAIN BOTTOM BAR */}
          <div className="bg-gray-100 border-t px-4 py-2 flex justify-between items-center">
            <div className="flex flex-col">
              <p className="text-base font-bold">₹{totals.totalPrice}</p>

              {totals.savings > 0 && (
                <p className="text-[0.65rem] text-green-600">
                  Saved ₹{totals.savings}
                </p>
              )}
            </div>

            <button
              onClick={handleProceed}
              disabled={isCTADisabled()}
              className="
          relative inline-flex items-center justify-center
          px-7 py-2 rounded-lg font-semibold text-white

          bg-gradient-to-r
          from-[#f0653a] to-[#e4572f]

          hover:from-[#e4572f] hover:to-[#d94d28]
          hover:shadow-lg hover:shadow-[#f0653a]/40

          shadow-md shadow-[#f0653a]/30

          transition-all duration-200 ease-out
          active:scale-[0.98]

          focus:outline-none
          focus:ring-2 focus:ring-[#f0653a]/60 focus:ring-offset-2

          disabled:opacity-50
          disabled:cursor-not-allowed
          disabled:shadow-none
        "
            >
              {getCTALabel()}
            </button>
          </div>
        </div>
      )}

      {/* PANEL FOOTER CTA (DESKTOP ONLY) */}
      {isPanel && step !== STEPS.PAYMENT && step !== STEPS.SUCCESS && (
        <div className="border-t px-4 py-3 bg-white">
          <button
            onClick={handleProceed}
            disabled={isCTADisabled()}
            className="
        w-full
        inline-flex items-center justify-center
        py-3 rounded-xl font-semibold
        text-white

        bg-gradient-to-r
        from-[#f0653a] to-[#e4572f]

        hover:from-[#e4572f] hover:to-[#d94d28]

        shadow-md shadow-[#f0653a]/30
        hover:shadow-lg hover:shadow-[#f0653a]/40

        transition-all duration-200 ease-out
        active:scale-[0.97]

        focus:outline-none
        focus:ring-2 focus:ring-[#f0653a]/60 focus:ring-offset-2

        disabled:opacity-50
        disabled:cursor-not-allowed
        disabled:shadow-none
      "
          >
            {getCTALabel()}
          </button>
        </div>
      )}

      {/* CONFIRM REMOVE MODAL */}
      <RemoveConfirmModal
        open={!!confirmItem}
        title={confirmItem?.title}
        onCancel={() => setConfirmItem(null)}
        onConfirm={confirmRemove}
      />
      <LoginModal
        isOpen={showLogin}
        onClose={() => setShowLogin(false)}
        onSuccess={() => {
          setShowLogin(false);
        }}
      />
    </div>
  );
};

export default UserServiceCart;

const RemoveConfirmModal = ({ open, title, onConfirm, onCancel }) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-4">
      <div className="bg-white rounded-xl w-full max-w-sm p-5 space-y-4">
        <h3 className="text-lg font-semibold">Remove service?</h3>

        <p className="text-sm text-gray-600">
          Are you sure you want to remove <b>{title}</b> from your cart?
        </p>

        <div className="flex justify-end gap-3">
          <button onClick={onCancel} className="px-4 py-2 border rounded-lg">
            Cancel
          </button>

          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-red-600 text-white rounded-lg"
          >
            Remove
          </button>
        </div>
      </div>
    </div>
  );
};

const EmptyCartView = ({ variant = "page" }) => {
  const navigate = useNavigate();

  return (
    <div
      className={`
    flex flex-col items-center justify-center text-center
    w-full min-w-0
    ${variant === "panel" ? "py-10 px-6" : "min-h-[90vh]"}
  `}
    >
      <div className="flex flex-col items-center justify-center  px-6">
        {/* Image container with subtle background */}
        <img
          src={sakhi_empty_cart_view}
          alt="Empty cart illustration"
          className="relative w-full h-48 object-contain"
        />

        {/* Text content with improved hierarchy */}
        <div className="space-y-3">
          <h3 className="text-center text-primaryTextColor font-philosopher text-xl font-semibold">
            Your cart is empty
          </h3>
          <p className="text-center text-secondaryTextColor text-sm leading-relaxed">
            Add some services to get started.
          </p>
        </div>
      </div>
      {variant === "panel" && (
        <div className="mt-2 text-xs text-gray-400">
          Select a service to continue
        </div>
      )}

      {variant === "page" && (
        <button
          onClick={() => navigate("/services")}
          className="
        mt-5 inline-flex items-center gap-2
        px-5 py-2.5 rounded-xl
        bg-accentTextColor text-white font-semibold
        hover:bg-accentTextColor/80 transition
        "
        >
          Browse Services
          <ArrowRight size={16} />
        </button>
      )}
    </div>
  );
};
